import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { discountOnRemove } from 'reactApp/appHelpers/featuresHelpers/features/discountOnRemove';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { getProductById } from 'reactApp/modules/products/products.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import {
    closeDiscountOnRemoveModal,
    DiscountOnRemoveActions,
    renderDiscountOnRemoveModal,
} from 'reactApp/ui/DiscountOnRemoveModal/DiscountOnRemoveModal.helper';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getPeriodName } from 'reactApp/utils/Period';
import { formatPrice } from 'reactApp/utils/priceHelpers';
import { channel } from 'redux-saga';
import { call, put, take } from 'redux-saga/effects';
import { select } from 'typed-redux-saga';

const source = 'overquota_transfer_files_trashbin';

export const sendDiscountOnRemoveAnalytic = (action: string) =>
    sendPaymentGa({
        action,
        eventCategory: ECategoryGa.modal,
        name: `mnz_${source}`,
    });

export function* handleRemoveAndShowDiscount() {
    // tempexp_19300-start
    const isPaidUser = yield* select(UserSelectors.isPaidUser);
    const isOverQuotaCloud = yield* select(UserSelectors.isOverQuotaUser);
    const isOverquotaFromProfileApi = yield* select(UserQuotaSelectors.isOverquotaBySpaceAndApi);
    const isOverQuotaMailAndCloud = yield* select(UserQuotaSelectors.isOverquota);
    const isOverQuota = isOverQuotaMailAndCloud || isOverQuotaCloud || isOverquotaFromProfileApi;

    const flowEnabled = !isPaidUser && isOverQuota && discountOnRemove.enabled;

    if (!flowEnabled) {
        return;
    }

    const discountChannel = channel();
    yield renderDiscountOnRemoveModal({
        onShow() {
            emitAnalyticEvent(AnalyticEventNames.DISCOUNT_ON_REMOVE_MODAL_SHOW);
            sendDiscountOnRemoveAnalytic('show');
        },
        onClose() {
            emitAnalyticEvent(AnalyticEventNames.DISCOUNT_ON_REMOVE_MODAL_CLOSE);
            sendDiscountOnRemoveAnalytic('close');
            discountChannel.put(DiscountOnRemoveActions.close);
        },
        onAction() {
            emitAnalyticEvent(AnalyticEventNames.DISCOUNT_ON_REMOVE_MODAL_CANCEL);
            sendDiscountOnRemoveAnalytic('click-reject');
            discountChannel.put(DiscountOnRemoveActions.cancel);
        },
        onSuccess() {
            emitAnalyticEvent(AnalyticEventNames.DISCOUNT_ON_REMOVE_MODAL_CLICK);
            sendDiscountOnRemoveAnalytic('click');
            discountChannel.put(DiscountOnRemoveActions.buy);
        },
    });

    const discountAction = yield take(discountChannel);
    discountChannel.close();
    closeDiscountOnRemoveModal();

    if (discountAction === DiscountOnRemoveActions.buy) {
        const isMobile = EnvironmentSelectors.isMobile();
        const productId = discountOnRemove.tariff;
        yield call(productsController.loadProducts);
        const product = yield* select(getProductById, productId);
        if (product) {
            yield put(
                openTariffBuy({
                    productId,
                    paySource: getPaySource('overquota_transfer_files_trashbin'),
                    paymentDescription:
                        discountOnRemove.isDiscount && product.discountPeriod
                            ? `+${product.space.value} на ${getPeriodName(product.discountPeriod)} со скидкой ${
                                  discountOnRemove.tariffDiscountValue
                              }%, далее ${formatPrice(product.price)} ₽ в ${getPeriodName(product.period)}`.replace(/\u00A0/g, ' ')
                            : undefined,
                    isMobile,
                })
            );
        } else {
            yield put(
                showSnackbarAction({
                    text: 'Нет доступных тарифов',
                    type: SnackbarTypes.failure,
                    id: 'discount-remove-error',
                    closable: true,
                })
            );
        }
    }

    return discountAction !== DiscountOnRemoveActions.cancel;
}
